// src/pages/EditRadiologistStatus.js
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useAuth from '../hooks/useAuth'; // custom hook
import '../styles/EmployeeMaster.css';

const styles = {
     form: {
          maxWidth: 900,
          margin: "20px auto",
          padding: 30,
          background: "#fff",
          borderRadius: 8,
          boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
          fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
          color: "#333",
     },
     section: {
          marginBottom: 30,
     },
     sectionHeader: {
          borderBottom: "2px solid #007bff",
          paddingBottom: 8,
          marginBottom: 20,
          color: "#007bff",
     },
     formGrid: {
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)", // 👈 exactly 4 equal-width columns
          gap: "20px 30px",
     },
     label: {
          display: "flex",
          flexDirection: "column",
          fontWeight: 600,
          fontSize: 14,
          color: "#444",
     },
     input: {
          marginTop: 6,
          padding: "10px 12px",
          fontSize: 14,
          border: "1.8px solid #ccc",
          borderRadius: 6,
          backgroundColor: "#fff",
          cursor: "text",
     },
     textarea: {
          marginTop: 6,
          padding: "10px 12px",
          fontSize: 14,
          lineHeight: "1.5",               // Add line-height for proper row sizing
          minHeight: "140px",              // Ensures at least 6 rows
          border: "1.8px solid #ccc",
          borderRadius: 6,
          backgroundColor: "#fff",
          cursor: "text",
          resize: "vertical",
          boxSizing: "border-box",         // Prevents overflow issues
     },
     table: {
          width: "100%",
          borderCollapse: "collapse",
          fontSize: 14,
     },
     th: {
          border: "1px solid #ddd",
          padding: "12px 15px",
          textAlign: "left",
          backgroundColor: "#007bff",
          color: "white",
          fontWeight: 600,
     },
     td: {
          border: "1px solid #ddd",
          padding: "8px 12px",
          textAlign: "left",
     },
     tbodyEvenRow: {
          backgroundColor: "#f7f7f7",
     },
     formActions: {
          display: "flex",
          justifyContent: "flex-end",
          gap: 15,
          marginTop: 20,
     },
     btnCancel: {
          padding: "10px 22px",
          borderRadius: 6,
          fontWeight: 600,
          fontSize: 15,
          cursor: "pointer",
          border: "none",
          backgroundColor: "#ddd",
          color: "#444",
     },
     btnSubmit: {
          padding: "10px 22px",
          borderRadius: 6,
          fontWeight: 600,
          fontSize: 15,
          cursor: "pointer",
          border: "none",
          backgroundColor: "#007bff",
          color: "white",
     },
     btnSubmitDisabled: {
          backgroundColor: "#5a9bf6",
          cursor: "not-allowed",
     },
};

const compactInputStyle = {
     padding: "4px 8px",
     fontSize: "13px",
     lineHeight: "1.2",
     height: "28px",
     border: "1px solid #ccc",
     borderRadius: "3px",
     boxSizing: "border-box",
};

const EditManagerRadiologistStatus = () => {
     const { id } = useParams();
     const { authFetch } = useAuth('/login');
     const navigate = useNavigate();
     const baseUrl = process.env.REACT_APP_BASE_URL;

     const [status, setStatus] = useState('');
     const [overallNotes, setOverallNotes] = useState('');
     const [candidateName, setCandidateName] = useState('');
     const [technical, setTechnical] = useState([]);
     const [error, setError] = useState('');
     const [saving, setSaving] = useState(false);
     const [notes, setNotes] = useState({ overallNotes: '' });

     const handleNotesChange = (e) => {
          const { name, value } = e.target;
          setNotes((prevNotes) => ({
               ...prevNotes,
               [name]: value,
          }));
     };


     const handleTechnicalChange = (id, field, value) => {
          setTechnical((prev) =>
               prev.map((item) =>
                    item.id === id ? { ...item, [field]: value } : item
               )
          );
     };

     // ✅ Fetch candidate info
     useEffect(() => {
          const fetchDetails = async () => {
               try {
                    const res = await authFetch(`${baseUrl}/getCandidateFullDetails/${id}`);
                    const data = await res.json();
                    if (data?.candidate) {
                         setCandidateName(data.candidate.candidate_name);
                         setTechnical(data.technical_evaluations || []);
                         setStatus(data.candidate.rpt_mgr_status || 'Pending');
                         setOverallNotes(data.candidate.overallNotes || 'Pending');
                    } else {
                         setError('Candidate not found.');
                    }
               } catch (err) {
                    setError('Error fetching candidate data');
               }
          };
          fetchDetails();
     }, [authFetch, baseUrl, id]);
     const user_code = sessionStorage.getItem('user_code');
     const user_name = sessionStorage.getItem('user_name');
     const handleSubmit = async (e) => {
          e.preventDefault();
          setSaving(true);
          setError('');
          try {
               const res = await authFetch(`${baseUrl}/updateManagerStatusId/${id}`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                         rpt_mgr_status: status,
                         reporting_manager: user_code,
                         rm_name: user_name,
                         overallNotes: overallNotes,
                         technical: technical,
                    }),

               });

               const result = await res.json();
               if (result.status) {
                    alert('Status updated successfully!');
                    navigate('/manager-radiology-assessment'); // 🔁 back to list
               } else {
                    setError(result.message || 'Failed to update status');
               }
          } catch (err) {
               setError('Submission error');
          } finally {
               setSaving(false);
          }
     };

     return (
          <div className="container mt-4">
               <h3>Update Status for: {candidateName}</h3>
               <form onSubmit={handleSubmit} className="mt-3">
                    <div className="form-group mb-3">
                         <label className="form-label">Reporting Manager Status</label>
                         <select
                              className="form-select"
                              value={status}
                              onChange={(e) => setStatus(e.target.value)}
                              disabled={saving}
                         >
                              <option value="Pending">Pending</option>
                              <option value="Approved">Approved</option>
                              <option value="Rejected">Rejected</option>
                         </select>
                    </div>



                    <section style={styles.section}>
                         <h2 style={styles.sectionHeader}>Technical Evaluations</h2>
                         <table style={styles.table}>
                              <thead>
                                   <tr>
                                        <th style={styles.th}>Particular</th>
                                        <th style={styles.th}>Assessment</th>
                                        <th style={styles.th}>Notes</th>
                                        <th style={styles.th}>Grading</th>
                                   </tr>
                              </thead>
                              <tbody>
                                   {technical.map((item, idx) => (
                                        <tr
                                             key={item.id}
                                             style={idx % 2 === 1 ? styles.tbodyEvenRow : undefined}
                                        >
                                             <td style={styles.td}>{item.particular}</td>

                                             <td style={styles.td}>
                                                  <select
                                                       value={item.assessment}
                                                       onChange={(e) =>
                                                            handleTechnicalChange(item.id, "assessment", e.target.value)
                                                       }
                                                       style={{ ...styles.input, ...compactInputStyle, margin: 0, padding: "0px 12px" }}
                                                  >
                                                       <option value="">-- Select --</option>
                                                       <option value="Good">Good</option>
                                                       <option value="Satisfactory">Satisfactory</option>
                                                       <option value="Dissatisfactory">Dissatisfactory</option>
                                                       <option value="Not Applicable">Not Applicable</option>
                                                  </select>
                                             </td>
                                             <td style={styles.td}>
                                                  <input
                                                       value={item.notes}
                                                       onChange={(e) =>
                                                            handleTechnicalChange(item.id, "notes", e.target.value)
                                                       }
                                                       style={{ ...styles.input, ...compactInputStyle, margin: 0 }}
                                                  />
                                             </td>
                                             {/* <td style={styles.td}><input
                    value={item.grading}
                    onChange={(e) =>
                      handleTechnicalChange(item.id, "grading", e.target.value)
                    }
                    style={{ ...styles.input, ...compactInputStyle, margin: 0 }}
                  /></td> */}
                                             <td style={styles.td}>
                                                  <div style={{ display: "flex", flexWrap: "wrap" }}>
                                                       {Array.from({ length: 11 }, (_, num) => num).map((num) => (
                                                            <label
                                                                 key={num}
                                                                 style={{
                                                                      display: "flex",
                                                                      alignItems: "center",
                                                                      gap: "4px",      // space between radio and number
                                                                      marginRight: "12px", // space between each radio-number pair
                                                                      fontSize: "12px",
                                                                      cursor: "pointer"
                                                                 }}
                                                            >
                                                                 <input
                                                                      type="radio"
                                                                      name={`grading-${item.id}`}
                                                                      value={num}
                                                                      checked={item.grading === String(num) || item.grading === num}
                                                                      onChange={() => handleTechnicalChange(item.id, "grading", num)}
                                                                 />
                                                                 {num}
                                                            </label>
                                                       ))}
                                                  </div>
                                             </td>


                                        </tr>
                                   ))}
                              </tbody>
                         </table>
                    </section>

                    <div className='form-group mb-3'>
                         <label className="form-label">
                              Overall Notes / Recommendation
                         </label>
                         <textarea
                              required
                              className="form-control input-form1"
                              name="overallNotes"
                              value={overallNotes}
                              onChange={(e) => setOverallNotes(e.target.value)}
                              rows={2}
                         />
                    </div>

                    {error && <div className="text-danger mb-2">{error}</div>}

                    <button type="submit" className="btn btn-primary" disabled={saving}>
                         {saving ? 'Saving...' : 'Update Status'}
                    </button>
               </form>
          </div>
     );
};

export default EditManagerRadiologistStatus;
